import { ResearchItemState } from '../../models/research.models';

/** Anything a finder lists that a member can pin. */
export interface PinnableItem {
    itemState: ResearchItemState;
}

/**
 * The current page's results with the pinned ones hoisted to the front
 * (Ruling R-F12; `parity/jobs.md §7.10` and the same rule on the grants and
 * collaborators screens — "Mockup sorts pinned rows to the top of the current
 * list, `pin(a,b) || …`").
 *
 * **Stable, and client-side only.** Both partitions keep the order the server
 * returned, so relevance ranking inside each half is untouched, and nothing
 * about the search or its GraphQL query changes: this reorders the results
 * already loaded for the page being shown. It is deliberately NOT a sort
 * comparator — `Array.prototype.sort` is only guaranteed stable in modern
 * engines and would still invite a second key later; two filters say exactly
 * what the mockup does and cannot drift.
 *
 * The Recommended tab does not use this: there, pinned items are lifted into
 * their own "Pinned by you" section instead (`PinnedItems` on each page).
 */
export function PinnedFirst<T extends PinnableItem>(items: readonly T[]): T[] {
    return PinnedFirstBy(items, (item) => item.itemState.pinned);
}

/**
 * `PinnedFirst` for rows whose pinned flag is not a top-level `itemState` — the
 * Saved board's rows carry it on their nested grant / person / job (QA
 * 2026-10-05: pinned saved grants sat at the bottom of the board). Same
 * stable two-partition rule.
 */
export function PinnedFirstBy<T>(items: readonly T[], isPinned: (item: T) => boolean): T[] {
    const pinned = items.filter(isPinned);
    if (pinned.length === 0 || pinned.length === items.length) return [...items];
    return [...pinned, ...items.filter((item) => !isPinned(item))];
}
